export interface RestrictedWordsState {
  words: Array<RestrictedWord>;
  searchWords: Array<RestrictedWord>;
  isLoading: boolean;
  groups: Array<RestrictedGroup>;
  selectedGroup: RestrictedGroup;
  globalWordsCount: number;
  wordSettings: WordSettings;
  groupSettings: GroupSettings;
  articles: Array<Article>;
}

export interface WordSettings extends RestrictedWord {
  restrictionGroups?: Array<RestrictedGroup>;
  exceptions?: Array<string>;
}

export type GroupSettings = RestrictedGroup;

export interface Exception {
  value: string;
}

export interface Article {
  id: number;
  name: string;
}

export interface RestrictedWordBase {
  id: number;
  policy: RestrictedWordPolicy;
}

export interface RestrictedWord extends RestrictedWordBase {
  value: string;
  groupsCount: number;
  exceptions?: Array<string>;
}

export enum RestrictedWordPolicy {
  PUBLISH_AND_MODERATE = 0,
  REQUIRE_APPROVAL = 1,
  REJECT = 2
}

export interface RestrictedWordResponse {
  id: number;
  word: string;
  policy: RestrictedWordPolicy;
  groups_count: number;
}
export interface RestrictionGroupsResponse {
  id: number;
  name: string;
  words_count: number;
}

export type FetchRestrictedWordsResponse = Array<RestrictedWordResponse>;

export type FetchRestrictedWordResponse = RestrictedWordResponse;

export type FetchGroupArticlesResponse = Array<Article>;

export interface RestrictedGroup {
  id: number;
  value: string;
  wordsCount: number;
  isAssigned?: boolean;
  policy?: RestrictedWordPolicy;
}

export interface RestrictedGroupResponse {
  id: number;
  text: string;
  words_count: number;
}

export type FetchRestrictedGroupsResponse = Array<RestrictedGroupResponse>;

export interface SetInitDataPayload {
  words: Array<RestrictedWord>;
  searchWords: Array<RestrictedWord>;
  groups: Array<RestrictedGroup>;
}

export interface UpdateWordPolicyPayload {
  groupId: number;
  word: RestrictedWord;
}

export interface DeleteGroupPayload extends AvailableForUndo {
  groupId: number;
  groupName?: string;
  deleteWordsFlag: boolean;
  articles?: Array<Article>;
  globalGroup?: RestrictedGroup;
}

export interface UpdateGroupPayload {
  groupId: number;
  name?: string;
  previousName?: string;
  updateGroupSucceededCallback?: (value: string) => void;
}

export interface AssignGroupToArticlePayload {
  postId: string;
  articleName: string;
  restrictedGroups: Array<RestrictedGroup>;
  undoRestrictedGroups?: Array<RestrictedGroup>;
  successCallback?: () => void;
}

export interface FetchRestrictedWordPayload {
  restrictedWordId: number;
}

export type AddRestrictedWordsResponse = Array<RestrictedWordResponse>;

export type AddRestrictionGroupsResponse = Array<RestrictionGroupsResponse>;

type DispatchSource = 'regular' | 'undo';

export interface AvailableForUndo {
  source?: DispatchSource;
}

export interface DeleteGlobalRestrictedWordsPayload extends AvailableForUndo {
  words: Array<RestrictedWord>;
}

export interface MakeWordsGlobalPayload {
  selectedGroupId: number;
  words: Array<RestrictedWord>;
  isWordSettingsFlow?: boolean;
}

export interface MakeWordsGlobalUndoPayload {
  initialWords: Array<RestrictedWord>;
  updatedWords: ResetGroupsResponse;
  selectedGroupId: number;
}

export interface MoveWordsToGroupPayload {
  groupId: number;
  words: Array<RestrictedWord>;
}

export interface AddVariousWordsToGlobalPayload {
  newWords: Array<string>;
  wordsWithGroups: Array<RestrictedWord>;
}

export interface AddVariousWordsToGlobalUndoPayload {
  newWords: Array<RestrictedWord>;
  wordsWithGroups: Array<RestrictedWord>;
  updatedWords: ResetGroupsResponse;
}

export type SettingsType = 'word' | 'group';

export interface AddRestrictedWordsToGroupPayload extends AvailableForUndo {
  groupId: number;
  newWords?: Array<string>;
  wordsWithGroups?: Array<RestrictedWord>;
  globalWords?: Array<RestrictedWord>;
}

export interface AddWordsToGroupUndoPayload {
  newWords: Array<RestrictedWord>;
  wordsWithGroups: Array<RestrictedWord>;
  globalWords: Array<RestrictedWord>;
  selectedGroupId: number;
}

export interface DeleteRestrictedWordsFromGroupPayload {
  groupId: number;
  words: Array<RestrictedWord>;
}

interface ResetGroupResponse {
  word_id: number;
  group_id: number;
  policy: RestrictedWordPolicy;
}

export type ResetGroupsResponse = Array<ResetGroupResponse>;

export interface DeleteWordsPayload {
  words: Array<RestrictedWord>;
  groupId: number;
  isWordSettingsFlow?: boolean;
}

export interface DeleteRestrictedWordsPayload extends AvailableForUndo {
  words: Array<RestrictedWord>;
  restrictionGroups: AddRestrictionGroupsPayload;
  exceptions: Array<string>;
  successToastMessage?: string;
  selectedGroup?: RestrictedGroup;
}

export interface UpdateRestrictedWordPayload extends AvailableForUndo {
  id?: number;
  text: string;
  policy: RestrictedWordPolicy;
  exceptions: Array<Exception> | Array<string>;
  restrictionGroups?: AddRestrictionGroupsPayload;
  restrictedWordForUndo?: WordSettings;
  updateWordSucceededCallback?: () => void;
  selectedGroup?: RestrictedGroup;
}

export interface AddRestrictionGroupsPayload {
  toCreate?: RestrictedGroup[];
  toAdd?: RestrictedGroup[];
  toDelete?: RestrictedGroup[];
}

export interface DeleteWordUndoPayload {
  word: Array<string>;
  restrictionGroups: AddRestrictionGroupsPayload;
  exceptions: Array<string>;
  selectedGroup: RestrictedGroup;
}
export interface UpdateWordUndoPayload {
  id: number;
  word: UpdateRestrictedWordPayload;
  restrictionGroups: AddRestrictionGroupsPayload;
  selectedGroup: RestrictedGroup;
}
